import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type KbPageRow, useKbPages, useKbSearch } from '../lib/kb';
import { usePagedQuery } from '../lib/paged';
import { KbPageIndex } from './KbPageIndex';
import { KbPagePanel } from './KbPagePanel';
import { KbSearchBar } from './KbSearchBar';
import { KbSearchHits } from './KbSearchHits';
import { LoadMore } from './LoadMore';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// Repo detail → Knowledge base (arc 101 slice 07 + the search route). The human
// door onto the wiki the agent maintains and reasons from: browse it, search it,
// read a page. Container — it owns the applied search, which slug is open, and
// the paging; the three presenters below paint rows.
//
// TWO READS, ONE SURFACE, and which one answers is the search box. Empty query ⇒
// the INDEX (`GET …/pages`, a real `Page` envelope over a DB-pushed keyset, so
// the Load-more is honest). A query ⇒ trigram HITS (`GET …/search`), which mint
// no cursor and get no Load-more — the route's ranked head has no page two to
// promise. The index query is not disabled while a search is applied: it was
// already fetched before the search existed, so keeping it cached is what makes
// Clear instant, and it costs no extra round trip.
//
// SEARCHING DOES NOT RESET THE INDEX. The two are separate reads with separate
// keys, so a search is not a filter over the pages already loaded — coming back
// lands on the same place in the wiki the reader left.
//
// PAGED BY `slug`, not `id`: a KB page has no row id on the wire (the slug IS
// its per-repo identity), and `usePagedQuery`'s default key would reconcile
// every row as undefined-keyed and remount the list on each page.
//
// `repoId` is a plain prop, not derived from route params — these routes address
// a repo by UUID, so the tab is mounted from the resolved repo row. Same
// convention as `<RepoSecretsTab/>`, and it keeps this directly testable.

export function RepoKbTab(props: {
  readonly repoId: string;
  readonly token: string | null;
}): JSX.Element {
  const [query, setQuery] = createSignal('');
  const [selected, setSelected] = createSignal<string | null>(null);

  const list = usePagedQuery<KbPageRow>(
    (cursor) => useKbPages(() => props.repoId, cursor, props.token),
    { key: 'slug' },
  );
  const search = useKbSearch(() => props.repoId, query, props.token);
  const searching = (): boolean => query() !== '';

  return (
    <div class="repo-detail__stack">
      <Panel
        title={t('repo_kb.title')}
        footer={searching() ? t('repo_kb.search_footer') : t('repo_kb.hint')}
        class="card--footered"
      >
        <KbSearchBar value={query()} onApply={setQuery} />

        <Show
          when={searching()}
          fallback={
            <QueryBoundary
              query={list.view}
              isEmpty={(rows) => rows.length === 0}
              loading={<Skeleton height="14rem" />}
              empty={<div class="list-empty">{t('repo_kb.empty')}</div>}
            >
              {(rows) => (
                <>
                  <KbPageIndex rows={rows} selected={selected()} onOpen={setSelected} />
                  <LoadMore list={list} label={t('repo_kb.load_more')} />
                </>
              )}
            </QueryBoundary>
          }
        >
          <QueryBoundary
            query={search}
            isEmpty={(data) => data.hits.length === 0}
            loading={<Skeleton height="14rem" />}
            empty={<div class="list-empty">{t('repo_kb.search_empty')}</div>}
          >
            {(data) => <KbSearchHits hits={data.hits} selected={selected()} onOpen={setSelected} />}
          </QueryBoundary>
        </Show>
      </Panel>

      <Show when={selected()}>
        {(slug) => (
          <KbPagePanel
            repoId={props.repoId}
            slug={slug()}
            token={props.token}
            onOpen={setSelected}
            onClose={() => setSelected(null)}
          />
        )}
      </Show>
    </div>
  );
}
